import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  role_id?: string | null;
  account_status?: string;
  school_id?: string | null;
  role?: string | null;
  sub_roles?: any[];
}

export function useUserProfile() {
  const {
    user,
    userProfile: authProfile,
    loading: authLoading,
  } = useAuth() as any;

  // Compute isAdmin synchronously to prevent state lag causing duplicate queries
  const isAdmin = useMemo(() => {
    if (!user || !authProfile) return false;

    const roleName = String((authProfile as any)?.role || "").toLowerCase();
    const subRoles = Array.isArray((authProfile as any)?.sub_roles)
      ? (authProfile as any).sub_roles
          .map((r: any) =>
            typeof r === "string"
              ? r
              : r?.name || r?.display_name || r?.role || "",
          )
          .filter(Boolean)
          .map((n: string) => n.toLowerCase())
      : [];

    const adminNames = new Set([
      "admin",
      "super admin",
      "administrator",
      "schulleitung",
    ]);
    return (
      adminNames.has(roleName) ||
      subRoles.some((r: string) => adminNames.has(r))
    );
  }, [user, authProfile]);

  const getDisplayName = () => {
    const profile = (authProfile as UserProfile | null) ?? null;

    if (!profile) return user?.email || "User";

    const firstName = profile.first_name || "";
    const lastName = profile.last_name || "";

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else {
      return user?.email || "User";
    }
  };

  // Compute isSecretary
  const isSecretary = useMemo(() => {
    if (!user || !authProfile) return false;

    const roleName = String((authProfile as any)?.role || "").toLowerCase();
    const subRoles = Array.isArray((authProfile as any)?.sub_roles)
      ? (authProfile as any).sub_roles
          .map((r: any) =>
            typeof r === "string"
              ? r
              : r?.name || r?.display_name || r?.role || "",
          )
          .filter(Boolean)
          .map((n: string) => n.toLowerCase())
      : [];

    return (
      roleName.includes("sekret") ||
      roleName.includes("secret") ||
      subRoles.some((r: string) => r.includes("sekret") || r.includes("secret"))
    );
  }, [user, authProfile]);

  return {
    userProfile: (authProfile as UserProfile | null) ?? null,
    loading: authLoading,
    error: null as string | null,
    isAdmin,
    isSecretary,
    displayName: getDisplayName(),
  };
}
